/**
 * 全局：鼠标悬停在被 CSS 截断的单元格/文案上时，自动补全原生 title。
 * - 仅在实际截断（scrollWidth/Height > client）时设置
 * - 不覆盖页面手写的 title（除非是本模块自动写入的）
 * - 跳过按钮、输入框、操作列等交互控件
 */

export const AUTO_ELLIPSIS_TITLE_ATTR = 'data-auto-ellipsis-title';

const SKIP_SELECTOR = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  'label',
  'option',
  '[contenteditable="true"]',
  '[data-no-auto-title]',
  '.temu-draft-actions',
  '.admin-temu-tpl-ops',
  '.tpo-certs-td--ops',
  '.tpo-certs-th--ops',
  '.tpo-certs-td--check',
  '.tpo-certs-th--check',
  '.tpo-certs-td--switch',
  '.tpo-certs-th--switch',
].join(',');

const CANDIDATE_SELECTOR = [
  'td',
  'th',
  '.tpo-certs-td',
  '.tpo-certs-th',
  '.spo-certs-td',
  '.spo-certs-th',
  '[class*="ellipsis"]',
  '[class*="truncate"]',
  '[class*="__cell"]',
  '[class*="-td"]',
  '[class*="-th"]',
].join(',');

function normalizeText(raw) {
  return String(raw || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isPlaceholder(text) {
  return !text || text === '—' || text === '-' || text === '–' || text === '…';
}

function isTruncated(el) {
  if (!el || el.nodeType !== 1) return false;
  // 允许 1px 误差（亚像素/滚动条）
  return el.scrollWidth > el.clientWidth + 1
    || el.scrollHeight > el.clientHeight + 1;
}

function isSkipped(el) {
  if (!el || !el.closest) return true;
  return Boolean(el.closest(SKIP_SELECTOR));
}

/**
 * 从事件目标向上找最合适的截断容器。
 * @param {EventTarget|null} start
 * @returns {HTMLElement|null}
 */
export function findEllipsisTitleTarget(start) {
  let el = start && start.nodeType === 3 ? start.parentElement : start;
  while (el && el !== document.body && el.nodeType === 1) {
    if (isSkipped(el)) return null;
    if (el.matches?.(CANDIDATE_SELECTOR)) {
      // 优先返回真正截断的祖先候选
      if (isTruncated(el)) return el;
      // 未截断则继续向上，避免挂在内层未溢出节点上
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * 为截断元素补全/清理自动 title。
 * @param {HTMLElement|null} el
 */
export function syncAutoEllipsisTitle(el) {
  if (!el || el.nodeType !== 1) return;
  if (isSkipped(el)) return;

  const wasAuto = el.hasAttribute(AUTO_ELLIPSIS_TITLE_ATTR);
  const existing = el.getAttribute('title');

  if (!isTruncated(el)) {
    if (wasAuto) {
      el.removeAttribute('title');
      el.removeAttribute(AUTO_ELLIPSIS_TITLE_ATTR);
    }
    return;
  }

  const text = normalizeText(el.innerText || el.textContent || '');
  if (isPlaceholder(text)) {
    if (wasAuto) {
      el.removeAttribute('title');
      el.removeAttribute(AUTO_ELLIPSIS_TITLE_ATTR);
    }
    return;
  }

  // 保留页面手写 title
  if (existing && !wasAuto) return;

  if (existing !== text) {
    el.setAttribute('title', text);
  }
  el.setAttribute(AUTO_ELLIPSIS_TITLE_ATTR, '1');
}

/**
 * 在 root 上挂载事件委托；返回卸载函数。
 * @param {ParentNode|Document} [root=document]
 * @returns {() => void}
 */
export function mountAutoEllipsisTitle(root = document) {
  const target = root && root.addEventListener ? root : document;

  const onOver = (event) => {
    const hit = findEllipsisTitleTarget(event.target);
    if (hit) syncAutoEllipsisTitle(hit);
  };

  // mouseover 冒泡，适合委托；focusin 覆盖键盘聚焦
  target.addEventListener('mouseover', onOver, true);
  target.addEventListener('focusin', onOver, true);

  return () => {
    target.removeEventListener('mouseover', onOver, true);
    target.removeEventListener('focusin', onOver, true);
  };
}
